import { skipCountdownRedirectsName } from "@circle-react/helpers/paywallCustomizationHelpers";
import type { PaywallCustomization } from "@circle-react/types/PaywallCustomization";
import { useEffect, useMemo, useState } from "react";

import { usePaywallCustomizationContext } from "../../PaywallCustomizationProvider";
import {
  formatCountdownTimer,
  getCookieFallback,
  getCountdownFinishAt,
} from "../helpers";

export interface UseCountdownOptions {
  countdownProperties: PaywallCustomization["countdown_properties"];
}

const TIMER_INTERVAL = 1000;

export const useCountdown = ({ countdownProperties }: UseCountdownOptions) => {
  const { paywallCustomization } = usePaywallCustomizationContext();

  const endDate = useMemo(
    () =>
      getCountdownFinishAt(paywallCustomization?.customizable_id) ||
      getCookieFallback(countdownProperties).finish_at,
    [paywallCustomization?.customizable_id, countdownProperties],
  );

  const [timeRemainingText, setTimeRemainingText] = useState("");
  const [exceedTime, setExceedTime] = useState<number>(0);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete(skipCountdownRedirectsName);
    window.history.replaceState({}, "", url);
  }, []);

  useEffect(() => {
    if (!endDate) {
      setTimeRemainingText("");
      return;
    }

    const updateTimer = () => {
      setTimeRemainingText(formatCountdownTimer(endDate, setExceedTime));
    };

    updateTimer();
    const interval = setInterval(updateTimer, TIMER_INTERVAL);

    return () => clearInterval(interval);
  }, [endDate]);

  return {
    isExpired: endDate ? new Date(endDate) <= new Date() : false,
    timeRemainingText,
    exceedTime,
  };
};
